import React, { useState, useEffect, useRef } from "react";
import { Check, Pencil, Trash2, X } from "lucide-react";
import { useLanguage } from '../context/LanguageContext';
import { useDialogs } from '../context/DialogContext';
import adminApi from "../adminApi";
import "./DatabasePreview.css";

interface TableData {
  data: any[];
  total: number;
}

// Kolumny cech w optionstable trzymają "Ja"/"Nein" (różna wielkość liter).
// Znacznik zamiast zwykłego tekstu - od razu widać, co jest włączone.
const jaNein = (value: any): "ja" | "nein" | null => {
  const text = String(value ?? "").trim().toLowerCase();
  if (text === "ja") return "ja";
  if (text === "nein") return "nein";
  return null;
};

const renderCellValue = (value: any): React.ReactNode => {
  if (typeof value === "boolean") return value ? "✔" : "—";
  const flaga = jaNein(value);
  if (flaga) {
    return <span className={`yes-no yes-no--${flaga}`}>{flaga === "ja" ? "Ja" : "Nein"}</span>;
  }
  const text = value?.toString() ?? "";
  return text === "" ? "—" : text;
};

// Pełna treść w tooltipie - długie kolumny (slogan, specialfeature) są przycięte
const cellTitle = (value: any, fallback: string): string => {
  const text = value === null || value === undefined ? "" : String(value);
  return text.length > 40 ? text : fallback;
};

function DatabasePreview() {
  const { t, tf } = useLanguage();
  const { notify, confirm } = useDialogs();
  const [tables, setTables] = useState<string[]>([]);
  const [activeTable, setActiveTable] = useState<string | null>(null);
  const [tableData, setTableData] = useState<TableData>({ data: [], total: 0 });
  // Tabela, której wiersze są w tableData. Przy odświeżaniu tej samej tabeli (zapis, usunięcie,
  // eksport, kolejna strona) wiersze zostają na ekranie - wcześniej znikały na czas ładowania,
  // tabela migała i przewijanie wracało na początek.
  const [loadedTable, setLoadedTable] = useState<string | null>(null);
  // Numer ostatniego żądania - spóźniona odpowiedź dla poprzedniej tabeli nie nadpisuje nowej
  const latestRequest = useRef(0);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const [, setPrimaryKey] = useState<string | null>(null);
  const [limit] = useState<number>(100);
  const [offset, setOffset] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editCell, setEditCell] = useState<{ row: number; column: string } | null>(null);
  const [editValue, setEditValue] = useState("");
  const [showEmptyColumns, setShowEmptyColumns] = useState(false);

  // Row edit state
  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  const [editingRowData, setEditingRowData] = useState<Record<string, any> | null>(null);

  // Lista tabel raz, przy wejściu do widoku
  useEffect(() => {
    fetchTables();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch table data when active table or offset changes
  // (fetchTableData celowo poza zależnościami - to zwykła funkcja, nowa przy każdym renderze)
  useEffect(() => {
    if (activeTable) {
      fetchTableData(activeTable, limit, offset);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTable, offset, limit]);

  const fetchTables = async () => {
    try {
      setLoading(true);
      const response = await adminApi.get("/database/tables");
      setTables(response.data);
      if (response.data.length > 0) {
        setActiveTable(response.data[0]);
        setOffset(0); // reset pagination when loading tables
      }
      setError(null);
    } catch (err) {
      console.error("Error fetching tables:", err);
      setError(t("Failed to load database tables"));
    } finally {
      setLoading(false);
    }
  };

  // try to detect a primary key column name from returned rows
  const detectPrimaryKey = (rows: any[]): string | null => {
    if (!rows || rows.length === 0) return null;
    const keys = Object.keys(rows[0] || {});
    // prefer exact 'id' (case-insensitive), then columns that endWith '_id', then first key named like 'ID' or similar
    const lowerKeys = keys.map(k => k.toLowerCase());
    const idxId = lowerKeys.indexOf("id");
    if (idxId >= 0) return keys[idxId];
    const idxUnderscoreId = lowerKeys.findIndex(k => k.endsWith("_id"));
    if (idxUnderscoreId >= 0) return keys[idxUnderscoreId];
    // fallback: common variations
    const variants = ["Id", "ID", "pk", "pk_id"];
    for (const v of variants) {
      const i = keys.indexOf(v);
      if (i >= 0) return keys[i];
    }
    // last resort: first key
    return keys[0] ?? null;
  };

  const fetchTableData = async (tableName: string, limit = 100, offset = 0) => {
    const requestId = ++latestRequest.current;
    try {
      setLoading(true);
      const response = await adminApi.get(
        `/database/tables/${tableName}`,
        { params: { limit, offset } }
      );
      if (requestId !== latestRequest.current) return;
      setTableData(response.data);
      setLoadedTable(tableName);
      // detect primary key from returned rows
      const pk = detectPrimaryKey(response.data.data || []);
      setPrimaryKey(pk);
      setError(null);
    } catch (err) {
      if (requestId !== latestRequest.current) return;
      console.error("Error fetching table data:", err);
      setError(t("Failed to load table data"));
      setPrimaryKey(null);
    } finally {
      if (requestId === latestRequest.current) setLoading(false);
    }
  };

  // Nowa strona od góry tabeli - ramka zostaje na ekranie (loadedTable), więc sama by się
  // nie przewinęła. Po zapisie / usunięciu przewinięcie zostaje tam, gdzie było.
  const scrollTableToTop = () => {
    if (tableWrapperRef.current) tableWrapperRef.current.scrollTop = 0;
  };

  const handlePrevPage = () => {
    setOffset((prev) => Math.max(0, prev - limit));
    scrollTableToTop();
  };

  const handleNextPage = () => {
    setOffset((prev) => prev + limit);
    scrollTableToTop();
  };

  const handleCellEdit = (rowIndex: number, column: string, currentValue: any) => {
    setEditCell({ row: rowIndex, column });
    setEditValue(currentValue?.toString() || "");
  };

  const handleSaveCell = async () => {
    if (!editCell || !activeTable) return;
    const row = tableData.data[editCell.row];
    // use the first column's name and value as the WHERE key
    const firstKey = Object.keys(row || {})[0];
    const idVal = firstKey ? row[firstKey] : undefined;
    if (!row || !firstKey || idVal === undefined || idVal === null) {
      notify(t("Cannot edit row: missing first-column primary value"), "error");
      setEditCell(null);
      return;
    }

    try {
      await adminApi.put(
        `/database/tables/${activeTable}`,
        {
          id: idVal,
          column: editCell.column,
          value: editValue,
          pkName: firstKey,
        }
      );

      // Odśwież dane tabeli
      await fetchTableData(activeTable, limit, offset);
      setEditCell(null);
    } catch (err) {
      console.error("Error updating cell:", err);
      setError(t("Failed to update cell"));
    }
  };

  const handleCancelEdit = () => {
    setEditCell(null);
    setEditValue("");
  };

  const handleDeleteRow = async (rowIndex: number) => {
    if (!activeTable) return;
    const row = tableData.data[rowIndex];
    const firstKey = Object.keys(row || {})[0];
    const idVal = firstKey ? row[firstKey] : undefined;

    if (!row || !firstKey || idVal === undefined || idVal === null) {
      notify(t("Cannot delete row: missing primary identifier"), "error");
      return;
    }

    if (!(await confirm({ message: t("Are you sure you want to delete this row?"), confirmLabel: t("Delete"), danger: true }))) return;

    try {
      setLoading(true);
      await adminApi.delete(`/database/tables/${activeTable}/rows`, {
        data: { id: idVal, pkName: firstKey }
      });
      await fetchTableData(activeTable, limit, offset);
    } catch (err) {
      console.error("Error deleting row:", err);
      setError(t("Failed to delete row"));
    } finally {
      setLoading(false);
    }
  };

  const handleClearTable = async () => {
    if (!activeTable) return;
    const confirmed = await confirm({
      message: tf("Permanently delete all data from table \"{table}\"? This cannot be undone.", { table: activeTable ?? "" }),
      confirmLabel: t("Clear Table"),
      danger: true,
    });
    if (!confirmed) return;

    try {
      setLoading(true);
      await adminApi.delete(`/database/tables/${activeTable}/clear`);
      setOffset(0);
      await fetchTableData(activeTable, limit, 0);
    } catch (err) {
      console.error("Error clearing table:", err);
      setError(t("Failed to clear table"));
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    if (!activeTable) return;
    try {
      setLoading(true);
      const response = await adminApi.get(`/database/tables/${activeTable}/export`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${activeTable}_export_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting CSV:", err);
      notify(t("Failed to export CSV. Make sure you are logged in."), "error");
    } finally {
      setLoading(false);
    }
  };

  // Row edit handlers
  const handleEditRow = (rowIndex: number) => {
    setEditingRowIndex(rowIndex);
    setEditingRowData({ ...(tableData.data[rowIndex] || {}) });
  };

  const handleChangeRowField = (field: string, value: any) => {
    setEditingRowData((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleSaveRow = async () => {
    if (editingRowIndex === null || !activeTable || !editingRowData) return;

    const originalRow = tableData.data[editingRowIndex];
    // use the first column as the identifying key
    const firstKey = Object.keys(originalRow || {})[0];
    const idVal = firstKey ? originalRow[firstKey] : undefined;
    if (!originalRow || !firstKey || idVal === undefined || idVal === null) {
      notify(t("Cannot edit row: missing first-column primary value"), "error");
      setEditingRowIndex(null);
      setEditingRowData(null);
      return;
    }

    // Determine changed fields
    const changedFields = Object.keys(editingRowData).filter(
      (k) => String(editingRowData[k]) !== String(originalRow[k])
    );

    if (changedFields.length === 0) {
      setEditingRowIndex(null);
      setEditingRowData(null);
      return;
    }

    try {
      setLoading(true);
      // Send individual column updates (backend expects id/column/value)
      await Promise.all(
        changedFields.map((col) =>
          adminApi.put(`/database/tables/${activeTable}`, {
            id: idVal,
            column: col,
            value: editingRowData[col],
            pkName: firstKey,
          })
        )
      );
      await fetchTableData(activeTable, limit, offset);
      setEditingRowIndex(null);
      setEditingRowData(null);
    } catch (err) {
      console.error("Error saving row:", err);
      setError(t("Failed to save row"));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRow = () => {
    setEditingRowIndex(null);
    setEditingRowData(null);
  };

  const renderTableContent = () => {
    // Napis "ładowanie" tylko wtedy, gdy nie ma jeszcze wierszy wybranej tabeli (pierwsze wejście,
    // zmiana zakładki). Odświeżenie tej samej tabeli zostawia ją na ekranie - patrz loadedTable.
    const hasCurrentData = activeTable !== null && loadedTable === activeTable;
    if (!hasCurrentData && (loading || (activeTable && !error))) {
      return <div className="loading">{t("Loading table data...")}</div>;
    }

    if (!hasCurrentData || tableData.data.length === 0) {
      return (
        <div className="empty-state">
          <p>{t("No data available in this table")}</p>
        </div>
      );
    }

    const allColumns = Object.keys(tableData.data[0]);
    const firstColumn = allColumns[0];
    const isEmptyValue = (value: any) =>
      value === null || value === undefined || String(value).trim() === "";
    // Kolumny puste we wszystkich wczytanych wierszach (np. options_config) - domyślnie ukryte,
    // z informacją nad tabelą. Pierwszej kolumny nie ukrywamy: to po niej zapisujemy wiersz.
    const emptyColumns = allColumns.filter(
      (col) => col !== firstColumn && tableData.data.every((row) => isEmptyValue(row[col]))
    );
    const columns = showEmptyColumns
      ? allColumns
      : allColumns.filter((col) => !emptyColumns.includes(col));
    // Kolumny z krótkimi wartościami (Ja/Nein, rok) i długą nazwą dostają pionowy nagłówek -
    // o szerokości decyduje wtedy wartość, a nie nazwa (w optionstable 14 takich kolumn
    // zajmowało po 100-130 px). Tylko w szerokich tabelach: w małych (pricetable,
    // optionsnrtable) miejsca jest dość, a pionowy nagłówek zajmowałby 186 px wysokości.
    const WIDE_TABLE_COLUMNS = 9;
    const narrowColumns = new Set(
      columns.length < WIDE_TABLE_COLUMNS
        ? []
        : columns.filter(
            (col) =>
              col !== firstColumn &&
              col.length > 5 &&
              tableData.data.every((row) => String(row[col] ?? "").trim().length <= 5)
          )
    );

    return (
      <>
      {emptyColumns.length > 0 && (
        <div className="db-preview-hint">
          {showEmptyColumns ? t("Empty columns shown:") : t("Empty columns hidden:")}{" "}
          <code>{emptyColumns.join(", ")}</code>
          <button type="button" className="db-preview-hint__toggle" onClick={() => setShowEmptyColumns((v) => !v)}>
            {showEmptyColumns ? t("Hide") : t("Show")}
          </button>
        </div>
      )}
      <div ref={tableWrapperRef} className={`table-wrapper${loading ? " is-busy" : ""}`} aria-busy={loading}>
        <table className={`data-table${narrowColumns.size > 0 ? " has-vertical" : ""}`}>
          <thead>
            <tr>
              {columns.map((col) => (
                <th key={col} className={narrowColumns.has(col) ? "is-vertical" : undefined} title={col}>
                  <span>{col}</span>
                </th>
              ))}
              <th className="col-actions">{t("Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {tableData.data.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {columns.map((col) => (
                  <td key={col} className={narrowColumns.has(col) ? "is-narrow" : undefined}>
                    {editingRowIndex === rowIndex ? (
                      <input
                        type="text"
                        value={editingRowData ? String(editingRowData[col] ?? "") : ""}
                        onChange={(e) => handleChangeRowField(col, e.target.value)}
                        title={`${t("Edit")} ${col}`}
                      />
                    ) : editCell?.row === rowIndex && editCell?.column === col ? (
                      <div className="edit-cell">
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") handleSaveCell();
                            if (e.key === "Escape") handleCancelEdit();
                          }}
                          autoFocus
                        />
                        <button className="btn-save-cell" onClick={handleSaveCell}>
                          ✓
                        </button>
                        <button className="btn-cancel-cell" onClick={handleCancelEdit}>
                          ✗
                        </button>
                      </div>
                    ) : (
                      <span
                        className="cell-content"
                        onDoubleClick={() => handleCellEdit(rowIndex, col, row[col])}
                        title={cellTitle(row[col], t("Double-click to edit"))}
                      >
                        {renderCellValue(row[col])}
                      </span>
                    )}
                  </td>
                ))}
                <td className="col-actions">
                  {editingRowIndex === rowIndex ? (
                    <div className="row-actions">
                      <button className="btn-save-row btn-icon" onClick={handleSaveRow} title={t("Save")} aria-label={t("Save")}>
                        <Check size={15} />
                      </button>
                      <button className="btn-cancel-row btn-icon" onClick={handleCancelRow} title={t("Cancel")} aria-label={t("Cancel")}>
                        <X size={15} />
                      </button>
                    </div>
                  ) : (
                    <div className="row-actions">
                      <button className="btn-edit-row btn-icon" onClick={() => handleEditRow(rowIndex)} title={t("Edit")} aria-label={t("Edit")}>
                        <Pencil size={15} />
                      </button>
                      <button className="btn-delete-row btn-icon" onClick={() => handleDeleteRow(rowIndex)} title={t("Delete row")} aria-label={t("Delete row")}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Stronicowanie i akcje pod ramką tabeli - wcześniej były w środku przewijanego obszaru */}
      <div className="table-info">
        <div className="pagination-controls">
          <button className="btn-prev" onClick={handlePrevPage} disabled={loading || offset === 0}>
            ◀ {t("Prev")}
          </button>
          <span className="pagination-info">
            {t("Showing")} {Math.min(offset + 1, tableData.total)} -{" "}
            {Math.min(offset + tableData.data.length, tableData.total)} {t("of")} {tableData.total}
          </span>
          <button
            className="btn-next"
            onClick={handleNextPage}
            disabled={loading || offset + tableData.data.length >= tableData.total}
          >
            {t("Next")} ▶
          </button>
          {loading && (
            <span className="db-preview-busy" role="status">
              {t("Updating...")}
            </span>
          )}
        </div>
        <div className="table-info-actions">
          <button className="btn-export-csv" onClick={handleExportCSV} disabled={loading}>
            {t("Export to CSV")}
          </button>
          <button className="btn-clear-table" onClick={handleClearTable} disabled={loading}>
            {t("Clear Table")}
          </button>
        </div>
      </div>
      </>
    );
  };

  // Zmiana tabeli: od pierwszej strony i bez edycji z poprzedniej tabeli
  const selectTable = (table: string) => {
    setActiveTable(table);
    setOffset(0);
    setEditCell(null);
    setEditingRowIndex(null);
    setEditingRowData(null);
    setError(null);
  };

  return (
    <div className="db-preview-container">
      <h2 className="db-preview-title">{t("Database Preview & Edit")}</h2>
      <p className="db-preview-subtitle">
        {t("View and edit database tables. Double-click any cell to edit its value.")}
      </p>

      <div className="tab-navigation">
        {tables.map((table) => (
          <button
            key={table}
            className={`tab-button ${activeTable === table ? "active" : ""}`}
            onClick={() => selectTable(table)}
          >
            {table}
          </button>
        ))}
      </div>

      {/* Błąd nad tabelą - wcześniej zastępował cały widok i nie dało się wrócić do tabeli */}
      {error && <div className="error db-preview-error">{error}</div>}

      {renderTableContent()}
    </div>
  );
}

export default DatabasePreview;